import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import * as moduleItemActions from '../module-item/module-item.actions';
import * as moduleActions from '../module/module.actions';
import { ModuleItemService } from '../../services/module-item/module-item.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { DashboardSnakeCasePipe } from '../../../core/pipes/dashboard-snake-case/dashboard-snake-case.pipe';
import { AppState } from '../../../state';

@Injectable()
export class ModuleItemEffects {
  getModuleItemsConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.getModuleItemsConfig),
      switchMap(() => {
        return this.moduleItemService.getModuleItemConfigs().pipe(
          map((moduleItemsConfig) =>
            moduleItemActions.getModuleItemsConfigSuccess({
              moduleItemsConfig: this.dashboardCamelCasePipe.keysToCamelCase(moduleItemsConfig),
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.getModuleItemsConfigError({ err })))
        );
      })
    )
  );

  createModuleItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.createModuleItem),
      switchMap(({ studyId, moduleId, moduleItem }) => {
        const moduleItemSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(moduleItem);
        return this.moduleItemService.createModuleItem(studyId, moduleId, moduleItemSnakeCase).pipe(
          map((newModuleItem) =>
            moduleItemActions.createModuleItemSuccess({
              newModuleItem: this.dashboardCamelCasePipe.keysToCamelCase(newModuleItem),
              studyId,
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.createModuleItemError({ err })))
        );
      })
    )
  );

  createModuleItemSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.createModuleItemSuccess),
        tap(({ studyId }) => {
          this.toastrService.success('Module item saved!');
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  createModuleItemError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.createModuleItemError),
        tap(({ err }) =>
          this.toastrService.error(`Error saving module item -  ${err.error.error || err.error.message}`)
        )
      ),
    { dispatch: false }
  );

  updateModuleItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.updateModuleItem),
      switchMap(({ studyId, moduleId, moduleItem }) => {
        const moduleItemSnakeCase = this.dashboardSnakeCasePipe.keysToSnakeCase(moduleItem);
        return this.moduleItemService.updateModuleItem(studyId, moduleId, moduleItemSnakeCase).pipe(
          map((updatedModuleItem) =>
            moduleItemActions.updateModuleItemSuccess({
              updatedModuleItem: this.dashboardCamelCasePipe.keysToCamelCase(updatedModuleItem),
              studyId,
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.updateModuleItemError({ err })))
        );
      })
    )
  );

  updateModuleItemSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.updateModuleItemSuccess),
        tap(({ studyId }) => {
          this.toastrService.success('Module item updated!');
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  updateModuleItemError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.updateModuleItemError),
        tap(({ err }) => this.toastrService.error(`Error updating module item ${err.error.error}`))
      ),
    { dispatch: false }
  );

  deleteModuleItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.deleteModuleItem),
      switchMap(({ studyId, moduleId, moduleItemId }) => {
        return this.moduleItemService.deleteModuleItem(studyId, moduleId, moduleItemId).pipe(
          map(() =>
            moduleItemActions.deleteModuleItemSuccess({
              studyId,
              moduleId,
              deletedModuleItemId: moduleItemId,
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.deleteModuleItemError({ err })))
        );
      })
    )
  );

  deleteModuleItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.deleteModuleItemSuccess),
      map(({ studyId, moduleId, deletedModuleItemId }) => {
        this.toastrService.success('Module item deleted!');
        return moduleActions.getModule({ studyId, moduleId });
      })
    )
  );

  deleteModuleItemError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.deleteModuleItemError),
        tap(({ err }) => {
          if (err.error?.cvs?.length > 0) {
            this.toastrService.error(`${err.error.error} <strong>` + err.error.cvs.join(', ') + '</strong>',
              '',
              {
                enableHtml: true,
                disableTimeOut: true,
              });
          } else {
            this.toastrService.error(`Error deleting module item ${err.error?.error}`);
          }
        })
      ),
    { dispatch: false }
  );

  saveModuleItemSortingOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.saveModuleItemSortingOrder),
      switchMap(({ studyId, moduleId, sortingOrder }) => {
        return this.moduleItemService.saveModuleItemSortingOrder(studyId, moduleId, sortingOrder).pipe(
          map((savedSortingOrder) =>
            moduleItemActions.saveModuleItemSortingOrderSuccess({
              studyId,
              moduleId,
              sortingOrder: savedSortingOrder,
            })
          ),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.saveModuleItemSortingOrderError({ err })))
        );
      })
    )
  );

  /**
   * Added getModule action to delete and saveSortingOrder module item effects,
   * so that the module store could be reloaded and populated with the latest module items.
   * This way dynamic text plugin inside module item dialog will always have the right values.
   */
  saveModuleItemSortingOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.saveModuleItemSortingOrderSuccess),
      map(({ studyId, moduleId, sortingOrder }) => {
        return moduleActions.getModule({ studyId, moduleId });
      })
    )
  );

  importModuleItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(moduleItemActions.importModuleItems),
      switchMap(({ moduleFile, idsForUpload }) => {
        return this.moduleItemService.importModuleItems(moduleFile, idsForUpload).pipe(
          map((importedModuleItems) => {
            importedModuleItems.items = importedModuleItems.items.map((item) =>
              this.dashboardCamelCasePipe.keysToCamelCase(item)
            );
            return moduleItemActions.importModuleItemsSuccess({ importedModuleItems });
          }),
          catchError((err: HttpErrorResponse) => of(moduleItemActions.importModuleItemsError({ err })))
        );
      })
    )
  );

  importModuleItemsSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.importModuleItemsSuccess),
        tap(({ importedModuleItems }) =>
          this.toastrService.success(`Successfully imported ${importedModuleItems?.rows} module items`)
        )
      ),
    { dispatch: false }
  );

  importModuleItemsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(moduleItemActions.importModuleItemsError),
        tap(({ err }) =>
          this.toastrService.error(
            `${err.error.error}`,
            'Unable to import module items from uploaded file. Please try again.'
          )
        )
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private moduleItemService: ModuleItemService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private dashboardSnakeCasePipe: DashboardSnakeCasePipe,
    private toastrService: ToastrService,
    private dialog: MatDialog
  ) {}
}
